/**
 * Centralised axios client for the buyer portal (M12).
 *
 * One axios instance, used everywhere. Replaces the ad-hoc
 * `fetch(...)` calls scattered across Calculator.jsx, InitialQuotations.jsx,
 * SellersChoice.jsx, PaymentDetails.jsx, AppointmentConfirmed.jsx, and
 * MyQuotations.jsx.
 *
 * What this gives us:
 *
 *   1. **Token injection.** A request interceptor reads the bearer
 *      token from `localStorage` on every call, so we never have to
 *      remember to set the Authorization header at the call site.
 *
 *   2. **401 → logout.** A response interceptor catches 401 from any
 *      authenticated request, clears the token, and dispatches a
 *      window event so AuthContext can react and redirect to /.
 *      We use a window event rather than importing AuthContext here
 *      to avoid the circular import.
 *
 *   3. **X-Request-ID propagation.** Every outbound request gets a
 *      fresh UUID in the `X-Request-ID` header so server logs can be
 *      correlated with the browser-side action that triggered them.
 *
 *   4. **Single base URL.** Read once from `REACT_APP_BACKEND_URL` at
 *      module load.
 *
 * Usage:
 *
 *   import api from '@/services/api';
 *
 *   const rfqs = await api.get('/my-quotations');
 *   const created = await api.post('/quotation-requests', payload);
 *
 * Note that paths are relative to `/api` — the base URL already
 * includes that prefix. Don't write `api.get('/api/my-quotations')`.
 */

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.error(
    "REACT_APP_BACKEND_URL is not set. Check apps/buyer-portal/frontend/.env",
  );
}

const API_BASE = `${BACKEND_URL || ""}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// -----------------------------------------------------------------------------
// Request interceptor — token + correlation id
// -----------------------------------------------------------------------------
api.interceptors.request.use((config) => {
  // Buyer portal has historically used `bidconvey_token` as the localStorage key
  // (different from the conveyancer portal's `token`). Preserve that.
  const token = localStorage.getItem("bidconvey_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    config.headers["X-Request-ID"] = crypto.randomUUID();
  }
  return config;
});

// -----------------------------------------------------------------------------
// Response interceptor — 401 → logout
// -----------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const wasAuthenticated = !!localStorage.getItem("bidconvey_token");
      if (wasAuthenticated) {
        localStorage.removeItem("bidconvey_token");
        delete api.defaults.headers.common.Authorization;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bc:auth:logout"));
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Extract a human-readable error message from an axios error.
 *
 * Handles three response shapes:
 *   1. Standard bc_shared.errors envelope: `{error: {code, message, request_id}}`
 *   2. Legacy FastAPI shape: `{detail: "..."}`
 *   3. Network / timeout errors: no response at all
 */
export function errorMessage(error) {
  if (!error) return "Unknown error";
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (data.error && typeof data.error === "object" && data.error.message) {
      return String(data.error.message);
    }
    if (data.detail) return String(data.detail);
  }
  if (error.message) return String(error.message);
  return "Unknown error";
}

/**
 * Extract the structured error code from an axios error.
 * Returns an empty string if not present.
 */
export function errorCode(error) {
  const data = error?.response?.data;
  if (data && typeof data === "object" && data.error && typeof data.error === "object") {
    return String(data.error.code || "");
  }
  return "";
}

export default api;
